import { useMemo, useState } from 'react';
import { SwapCalls } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';
import useCampaigns from 'hooks/queries/useCampaigns';
import useContacts from 'hooks/queries/useContacts';
import useCurationsQueryParams from 'hooks/useCurationsQueryParams';
import useDebounce from 'hooks/useDebounce';
import useContactsFileUploadProps from 'domains/contacts/hooks/useContactsFileUploadProps';
import ContactsUploadDialog from 'domains/contacts/components/ContactsUploadDialog';
import FileUploadButton from 'domains/contacts/components/FileUploadButton';
import Button from 'domains/core/components/Button';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import StatusFilterDropdown from 'domains/core/components/StatusFilterDropdown';
import { MultiSelectOption } from 'domains/core/components/MultiSelectDropdown';
import PageLayout from 'domains/core/components/PageLayout';
import SearchField from 'domains/core/components/SearchField';
import { CampaignStatus, URLPaths } from 'models/enums';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { SEARCH_DEBOUNCE_MS } from 'utils';
import CampaignsTable from './CampaignsTable';

const toLabel = (status: string): string =>
    status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\bab\b/g, 'A/B')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const abTestStatuses = new Set<CampaignStatus>([
    CampaignStatus.SENDING_AB_TEST,
    CampaignStatus.PENDING_WINNER_SELECTION,
    CampaignStatus.WINNER_DECLARED,
    CampaignStatus.WINNER_SCHEDULED,
    CampaignStatus.SENDING_WINNER,
    CampaignStatus.WINNER_SENT,
    CampaignStatus.WINNER_FAILED,
    CampaignStatus.WINNER_STOPPED,
]);

const allCampaignStatusOptions: MultiSelectOption<CampaignStatus>[] = Object.values(CampaignStatus)
    .map((status) => ({ value: status, label: toLabel(status) }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const CampaignsPage = () => {
    const history = useHistory();
    const { canManageCurationsDrafts, canUploadContacts } = useAccessCentrePermissions();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
    const { statuses, changeViewParams } = useCurationsQueryParams();
    const { aBTesting: hasABTesting, statusFilterCuration: hasStatusFilterCuration } = useFlags();

    const campaignStatusOptions = useMemo(
        () =>
            hasABTesting
                ? allCampaignStatusOptions
                : allCampaignStatusOptions.filter((o) => !abTestStatuses.has(o.value)),
        [hasABTesting]
    );

    const {
        data: campaigns,
        isLoading: isCampaignsLoading,
        isRefetching: isCampaignsRefetching,
        isSuccess: isCampaignsSuccess,
        refetch: refetchCampaigns,
    } = useCampaigns({
        searchTerm: debouncedSearchTerm,
        statuses,
        reactQueryOptions: {
            refetchInterval: 30000,
        },
    });
    const { data: contacts, isSuccess: isContactsSuccess } = useContacts({
        options: { limit: '1' },
    });

    const fileUploadProps = useContactsFileUploadProps();

    const isContactsEmpty = !contacts?.length;

    const handleStatusFilterApply = (newStatuses: CampaignStatus[]) => {
        changeViewParams({ newStatuses });
    };

    if (isContactsSuccess && isContactsEmpty) {
        return (
            <EmptyStateLayout
                caption="curations"
                header="create authentic connections"
                guidingText="Once your contacts are uploaded, build your first Curation to begin communicating with your contacts."
            >
                {canUploadContacts && (
                    <>
                        <ContactsUploadDialog {...fileUploadProps} />
                        <FileUploadButton {...fileUploadProps} />
                    </>
                )}
            </EmptyStateLayout>
        );
    }

    return (
        <PageLayout
            header="curations"
            headerAction={
                canManageCurationsDrafts ? (
                    <Button
                        endIcon={<SwapCalls />}
                        onClick={() => history.push(URLPaths.CURATIONS_LANDING)}
                        variant="outlined"
                    >
                        Create
                    </Button>
                ) : undefined
            }
            headerIcon={<SwapCalls fontSize="inherit" />}
            headerOthers={
                <Stack direction="row" spacing={4} alignItems="center">
                    {hasStatusFilterCuration && (
                        <StatusFilterDropdown
                            options={campaignStatusOptions}
                            items={campaigns ?? []}
                            selectedValues={statuses}
                            onApply={handleStatusFilterApply}
                            label="Status"
                            testIdPrefix="campaign-status-filter"
                            menuId="campaign-status-filter-menu"
                            maxHeight="400px"
                            disabled={isCampaignsLoading || isCampaignsRefetching}
                        />
                    )}
                    <SearchField
                        placeholder="Search curations..."
                        value={searchTerm}
                        onChange={setSearchTerm}
                        disabled={isCampaignsLoading || isCampaignsRefetching}
                        dataTestId="curations-search-input"
                    />
                </Stack>
            }
        >
            {(isCampaignsLoading || isCampaignsSuccess) && (
                <CampaignsTable
                    campaigns={campaigns ?? []}
                    isLoading={isCampaignsLoading}
                    isRefetching={isCampaignsRefetching}
                    searchTerm={debouncedSearchTerm}
                    statuses={statuses}
                    refetch={refetchCampaigns}
                />
            )}
        </PageLayout>
    );
};

export default CampaignsPage;
